import { useCallback, useEffect, useMemo, useState } from 'react';
import { notification } from 'antd';
import CustomerIconOutlined from 'components/icons/CustomerIconOutlined';
import Button from 'components/new-ui/base/button/Button';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import CustomerAutocomplete from 'components/new-ui/composed/selects/CustomerAutocomplete';
import useCustomers from 'hooks/useCustomers';
import { useField, useFormikContext } from 'formik';
import { Customer as CustomerType } from 'store/customers/customer';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import useDebounce from 'utils/useDebounce';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import { getCustomerFieldText } from 'components/ui/composed/selects/utils';
import { CreateJobFormFields } from 'views/jobs/validationSchemas';
import { CreateJobFormSteps } from 'views/jobs/types';
import CreateNewCustomerDrawer from 'views/customers/components/create-customer-drawer/CreateNewCustomerDrawer';

const fieldName = 'customer.customerId';

const CustomerSection = () => {
    const { customers, listCustomers } = useCustomers();
    const [_, meta, helpers] = useField(fieldName);
    const { currentStepConfig, isIdle, isComplete, isActive, resetStep, lastResetEvent, stepsConfig } =
        useMultiStepFormContext<CreateJobFormFields>();
    const { validateForm } = useFormikContext<CreateJobFormFields>();

    const [selectedCustomer, setSelectedCustomer] = useState<CustomerType | null>(null);
    const [showCreateCustomerDrawer, setShowCreateCustomerDrawer] = useState(false);

    const disabled = currentStepConfig?.disabled ?? false;

    const stepKey = CreateJobFormSteps.CUSTOMER;
    const currentStepIndex = useMemo(() => stepsConfig.findIndex((s) => s.key === stepKey), [stepKey, stepsConfig]);

    const querySearchCustomers = useCallback(
        async (query: string) => {
            if (query) {
                const params = { query };
                await listCustomers(params);
            } else {
                await listCustomers({ size: 200 });
            }
        },
        [listCustomers]
    );

    const debouncedSearchCustomers = useDebounce(querySearchCustomers, 500);

    const selectCustomer = async (customer: CustomerType) => {
        try {
            setSelectedCustomer(customer);
            helpers.setValue(customer.id);
            validateForm();
        } catch (err) {
            notification.error({
                message: 'Could not update job with this customer',
            });
            setSelectedCustomer(null);
        }
    };

    const handleSelect = async (id: string) => {
        helpers.setTouched(true);

        const customer = customers.list.find((c) => c.id === id);

        if (!customer) {
            return;
        }

        await selectCustomer(customer);
        // Refresh the customers in the store
        listCustomers({ size: 200 });
    };

    const handleSearch = (val: string) => {
        if (selectedCustomer) {
            return;
        }

        debouncedSearchCustomers(val);
    };

    const handleOnCreateCustomer = async (customer: CustomerType) => {
        if (!customer.id) {
            console.log('Could not create customer');
            return;
        }

        selectCustomer(customer);
        setShowCreateCustomerDrawer(false);
    };

    const handleReset = () => {
        resetStep(stepKey, 'customerId');
        setSelectedCustomer(null);
    };

    const readonlyValue = selectedCustomer ? getCustomerFieldText(selectedCustomer) : '';
    const selectValue = selectedCustomer ? getCustomerFieldText(selectedCustomer) : undefined;

    // Reset the field if some of the previous form sections have been reset
    useEffect(() => {
        if (lastResetEvent?.stepIndex == null) return;

        if (lastResetEvent.stepIndex > -1 && lastResetEvent.stepIndex < currentStepIndex) {
            setSelectedCustomer(null);
            helpers.setValue(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResetEvent, currentStepIndex]);

    if (isIdle(stepKey)) {
        return <FormSectionHeader title="Customer" icon={<CustomerIconOutlined />} />;
    }

    if (isComplete(stepKey)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormSectionHeader title="Customer" icon={<CustomerIconOutlined />} />
                <ReadonlyFormItem disabled={disabled} onReset={handleReset} value={readonlyValue} />
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <div>
                <FormSectionHeader
                    title="Customer"
                    icon={<CustomerIconOutlined />}
                    subtitle="You can create a new customer or search for an existing one"
                    button={
                        <Button disabled={disabled} onClick={() => setShowCreateCustomerDrawer(true)}>
                            Create Customer
                        </Button>
                    }
                />
                <CustomerAutocomplete
                    inputStyle={{ width: '100%' }}
                    customers={customers.list}
                    disabled={disabled}
                    handleSelectCustomer={handleSelect}
                    search={handleSearch}
                    setCustomer={setSelectedCustomer}
                    onBlur={() => helpers.setTouched(true)}
                    fieldMeta={meta}
                    value={selectValue}
                />
                <CreateNewCustomerDrawer
                    open={showCreateCustomerDrawer}
                    onClose={() => setShowCreateCustomerDrawer(false)}
                    handleOk={handleOnCreateCustomer}
                    refreshCustomers={listCustomers}
                />
            </div>
        );
    }

    return null;
};

export default CustomerSection;
